// A minimal ISO-BMFF (MP4) writer, prober and remuxer for ONE audio track — what a voice note
// needs, and nothing else.
//
// Why it exists: the apps record AAC in an .m4a (VoiceRecorder.swift: kAudioFormatMPEG4AAC, mono,
// 44.1 kHz) because it is the one format every client plays natively. AVFoundation cannot play
// WebM/Opus, and AVAudioPlayer is unreliable with FRAGMENTED MP4 (what MediaRecorder writes), so
// the web writes a classic progressive file — ftyp, moov with complete sample tables, mdat — the
// same layout AVAudioRecorder and Android's MediaRecorder produce.
//
// Pure (bytes in, bytes out), unit-tested in mp4Audio.test.ts; no browser API is touched here.

export interface Mp4Sample {
  data: Uint8Array;
  /** In the track's timescale (1024 for one AAC frame at timescale = sample rate). */
  duration: number;
}

// MARK: - Writing

const enc = new TextEncoder();

function u8(n: number): number[] {
  return [n & 0xff];
}
function u16(n: number): number[] {
  return [(n >>> 8) & 0xff, n & 0xff];
}
function u32(n: number): number[] {
  return [(n >>> 24) & 0xff, (n >>> 16) & 0xff, (n >>> 8) & 0xff, n & 0xff];
}
function fourcc(s: string): number[] {
  return Array.from(enc.encode(s.slice(0, 4).padEnd(4, ' ')));
}

type Part = Uint8Array | number[];

function concat(parts: Part[]): Uint8Array {
  let length = 0;
  for (const p of parts) length += p.length;
  const out = new Uint8Array(length);
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}

function box(type: string, ...parts: Part[]): Uint8Array {
  const body = concat(parts);
  return concat([u32(body.length + 8), fourcc(type), body]);
}

function fullBox(type: string, version: number, flags: number, ...parts: Part[]): Uint8Array {
  return box(type, [version & 0xff, (flags >>> 16) & 0xff, (flags >>> 8) & 0xff, flags & 0xff], ...parts);
}

const MATRIX = [...u32(0x00010000), ...u32(0), ...u32(0), ...u32(0), ...u32(0x00010000), ...u32(0), ...u32(0), ...u32(0), ...u32(0x40000000)];

/** AAC sampling-frequency index (ISO 14496-3 Table 1.18); 15 = explicit (not used here). */
const AAC_RATES = [96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050, 16000, 12000, 11025, 8000, 7350];

/** The two-byte AudioSpecificConfig of AAC-LC (object type 2) for a rate and channel count. */
export function audioSpecificConfig(sampleRate: number, channels: number): Uint8Array {
  const index = AAC_RATES.indexOf(sampleRate);
  if (index < 0) throw new Error(`AAC: unsupported sample rate ${sampleRate}`);
  const objectType = 2;
  return new Uint8Array([(objectType << 3) | (index >> 1), ((index & 1) << 7) | ((channels & 0x0f) << 3)]);
}

/** An MPEG-4 descriptor: tag, then the size in 7-bit groups (one byte for everything under 128). */
function descriptor(tag: number, body: Uint8Array): Uint8Array {
  const size: number[] = [body.length & 0x7f];
  for (let n = body.length >>> 7; n > 0; n >>>= 7) size.unshift((n & 0x7f) | 0x80);
  return concat([[tag, ...size], body]);
}

/** The `mp4a` sample entry with its `esds` — AAC as AVFoundation, Android and browsers read it. */
export function mp4aSampleEntry(opts: { sampleRate: number; channels: number; asc: Uint8Array; avgBitrate: number; maxBitrate?: number }): Uint8Array {
  const decoderSpecific = descriptor(0x05, opts.asc);
  const decoderConfig = descriptor(
    0x04,
    concat([
      u8(0x40), // objectTypeIndication: MPEG-4 Audio
      u8((0x05 << 2) | 1), // streamType audio, upstream 0, reserved 1
      [0, 0x18, 0], // bufferSizeDB
      u32(opts.maxBitrate ?? opts.avgBitrate),
      u32(opts.avgBitrate),
      decoderSpecific,
    ]),
  );
  const sl = descriptor(0x06, new Uint8Array([0x02]));
  const es = descriptor(0x03, concat([u16(1), u8(0), decoderConfig, sl]));
  const esds = fullBox('esds', 0, 0, es);
  // The sample rate field is 16.16 fixed point and only holds rates under 65536 — every AAC rate
  // a voice note uses.
  return box('mp4a', [0, 0, 0, 0, 0, 0], u16(1), u32(0), u32(0), u16(opts.channels), u16(16), u16(0), u16(0), u32((opts.sampleRate & 0xffff) << 16), esds);
}

/** stts: consecutive equal durations collapse into one entry. */
function stts(samples: Mp4Sample[]): Uint8Array {
  const runs: Array<[number, number]> = [];
  for (const s of samples) {
    const last = runs[runs.length - 1];
    if (last && last[1] === s.duration) last[0] += 1;
    else runs.push([1, s.duration]);
  }
  return fullBox('stts', 0, 0, u32(runs.length), ...runs.map(([count, delta]) => [...u32(count), ...u32(delta)]));
}

/**
 * A progressive ("fast start") MP4 holding one audio track: ftyp, moov, mdat — moov first, so a
 * player can start before the whole file has arrived. All samples sit in one chunk.
 */
export function writeAudioMp4(opts: { timescale: number; sampleEntry: Uint8Array; samples: Mp4Sample[]; majorBrand?: string }): Uint8Array {
  const { timescale, sampleEntry, samples } = opts;
  const mediaDuration = samples.reduce((sum, s) => sum + s.duration, 0);
  const movieTimescale = 1000;
  const movieDuration = Math.round((mediaDuration / timescale) * movieTimescale);
  const brand = opts.majorBrand ?? 'M4A ';
  const ftyp = box('ftyp', fourcc(brand), u32(0), fourcc('M4A '), fourcc('isom'), fourcc('mp42'));

  const build = (chunkOffset: number) => {
    const stbl = box(
      'stbl',
      fullBox('stsd', 0, 0, u32(1), sampleEntry),
      stts(samples),
      fullBox('stsc', 0, 0, u32(samples.length ? 1 : 0), ...(samples.length ? [[...u32(1), ...u32(samples.length), ...u32(1)]] : [])),
      fullBox('stsz', 0, 0, u32(0), u32(samples.length), ...samples.map((s) => u32(s.data.length))),
      fullBox('stco', 0, 0, u32(samples.length ? 1 : 0), ...(samples.length ? [u32(chunkOffset)] : [])),
    );
    const minf = box('minf', fullBox('smhd', 0, 0, u16(0), u16(0)), box('dinf', fullBox('dref', 0, 0, u32(1), fullBox('url ', 0, 1))), stbl);
    const mdia = box(
      'mdia',
      fullBox('mdhd', 0, 0, u32(0), u32(0), u32(timescale), u32(mediaDuration), u16(0x55c4) /* 'und' */, u16(0)),
      fullBox('hdlr', 0, 0, u32(0), fourcc('soun'), u32(0), u32(0), u32(0), Array.from(enc.encode('SoundHandler\0'))),
      minf,
    );
    const tkhd = fullBox('tkhd', 0, 0x000003, u32(0), u32(0), u32(1), u32(0), u32(movieDuration), u32(0), u32(0), u16(0), u16(0), u16(0x0100), u16(0), MATRIX, u32(0), u32(0));
    const mvhd = fullBox('mvhd', 0, 0, u32(0), u32(0), u32(movieTimescale), u32(movieDuration), u32(0x00010000), u16(0x0100), u16(0), u32(0), u32(0), MATRIX, new Array(24).fill(0), u32(2));
    return box('moov', mvhd, box('trak', tkhd, mdia));
  };
  // The chunk offset depends on moov's size, which does not depend on the offset's value.
  const moovSize = build(0).length;
  const moov = build(ftyp.length + moovSize + 8);
  const payload = concat(samples.map((s) => s.data));
  const mdat = concat([u32(payload.length + 8), fourcc('mdat'), payload]);
  return concat([ftyp, moov, mdat]);
}

// MARK: - Reading

interface Box {
  type: string;
  start: number;
  /** Where the payload starts (after size/type/largesize). */
  body: number;
  end: number;
}

const dec = new TextDecoder();

function view(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

function children(bytes: Uint8Array, start: number, end: number): Box[] {
  const dv = view(bytes);
  const out: Box[] = [];
  let at = start;
  while (at + 8 <= end) {
    let size = dv.getUint32(at);
    const type = dec.decode(bytes.subarray(at + 4, at + 8));
    let body = at + 8;
    if (size === 1) {
      if (at + 16 > end) break;
      size = Number(dv.getBigUint64(at + 8));
      body = at + 16;
    } else if (size === 0) {
      size = end - at;
    }
    if (size < 8 || at + size > end) break;
    out.push({ type, start: at, body, end: at + size });
    at += size;
  }
  return out;
}

function find(bytes: Uint8Array, parent: Box | null, path: string[]): Box | null {
  let current: Box | null = parent;
  for (const type of path) {
    const list: Box[] = children(bytes, current ? current.body : 0, current ? current.end : bytes.length);
    current = list.find((b) => b.type === type) ?? null;
    if (!current) return null;
  }
  return current;
}

/** Reads one descriptor header (tag + variable-length size) at `at`. */
function readDescriptor(bytes: Uint8Array, at: number): { tag: number; body: number; end: number } | null {
  if (at >= bytes.length) return null;
  const tag = bytes[at];
  let size = 0;
  let i = at + 1;
  for (let n = 0; n < 4 && i < bytes.length; n++) {
    const b = bytes[i++];
    size = (size << 7) | (b & 0x7f);
    if (!(b & 0x80)) break;
  }
  return { tag, body: i, end: i + size };
}

export interface Mp4Probe {
  majorBrand: string;
  /** moof boxes present (MediaRecorder output). */
  fragmented: boolean;
  /** The sample entry's fourcc: 'mp4a' for AAC, 'Opus' for Opus. */
  codec: string;
  /** esds objectTypeIndication (0x40 = MPEG-4 audio) — null when the entry has no esds. */
  objectTypeIndication: number | null;
  /** AudioSpecificConfig audio object type (2 = AAC-LC). */
  audioObjectType: number | null;
  channels: number;
  sampleRate: number;
  timescale: number;
  sampleCount: number;
  durationSeconds: number;
}

/** What a file is, read from its boxes — the in-test ffprobe. Null when it is not an MP4 at all. */
export function probeMp4(bytes: Uint8Array): Mp4Probe | null {
  const top = children(bytes, 0, bytes.length);
  const ftyp = top.find((b) => b.type === 'ftyp');
  const moov = top.find((b) => b.type === 'moov');
  if (!ftyp || !moov) return null;
  const dv = view(bytes);
  const trak = find(bytes, moov, ['trak']);
  const mdhd = trak && find(bytes, trak, ['mdia', 'mdhd']);
  const stbl = trak && find(bytes, trak, ['mdia', 'minf', 'stbl']);
  const stsd = stbl && find(bytes, stbl, ['stsd']);
  if (!mdhd || !stbl || !stsd) return null;
  const mdhdVersion = bytes[mdhd.body];
  const timescale = mdhdVersion === 1 ? dv.getUint32(mdhd.body + 20) : dv.getUint32(mdhd.body + 12);
  // stsd: fullbox header (4) + entry count (4), then the first sample entry box.
  const entry = children(bytes, stsd.body + 8, stsd.end)[0];
  if (!entry) return null;
  const channels = dv.getUint16(entry.body + 16);
  let sampleRate = dv.getUint32(entry.body + 24) >>> 16;
  let objectTypeIndication: number | null = null;
  let audioObjectType: number | null = null;
  const esds = children(bytes, entry.body + 28, entry.end).find((b) => b.type === 'esds');
  if (esds) {
    const es = readDescriptor(bytes, esds.body + 4);
    if (es && es.tag === 0x03) {
      const flags = bytes[es.body + 2];
      let at = es.body + 3 + (flags & 0x80 ? 2 : 0) + (flags & 0x20 ? 1 + bytes[es.body + 3] : 0) + (flags & 0x40 ? 2 : 0);
      const dcd = readDescriptor(bytes, at);
      if (dcd && dcd.tag === 0x04) {
        objectTypeIndication = bytes[dcd.body];
        at = dcd.body + 13;
        const dsi = readDescriptor(bytes, at);
        if (dsi && dsi.tag === 0x05 && dsi.end - dsi.body >= 2) {
          audioObjectType = bytes[dsi.body] >> 3;
          const index = ((bytes[dsi.body] & 0x07) << 1) | (bytes[dsi.body + 1] >> 7);
          if (AAC_RATES[index]) sampleRate = AAC_RATES[index];
        }
      }
    }
  }
  const fragmented = top.some((b) => b.type === 'moof');
  let sampleCount = 0;
  let mediaDuration = 0;
  if (fragmented) {
    for (const f of readFragments(bytes, top, timescale)) {
      sampleCount += 1;
      mediaDuration += f.duration;
    }
  } else {
    const sttsBox = find(bytes, stbl, ['stts']);
    if (sttsBox) {
      const n = dv.getUint32(sttsBox.body + 4);
      for (let i = 0; i < n; i++) {
        const count = dv.getUint32(sttsBox.body + 8 + i * 8);
        sampleCount += count;
        mediaDuration += count * dv.getUint32(sttsBox.body + 12 + i * 8);
      }
    }
  }
  return {
    majorBrand: dec.decode(bytes.subarray(ftyp.body, ftyp.body + 4)),
    fragmented,
    codec: entry.type,
    objectTypeIndication,
    audioObjectType,
    channels,
    sampleRate,
    timescale,
    sampleCount,
    durationSeconds: timescale > 0 ? mediaDuration / timescale : 0,
  };
}

/** Every sample of a fragmented file, in order: bytes + duration (track 1 only). */
function readFragments(bytes: Uint8Array, top: Box[], _timescale: number): Mp4Sample[] {
  const dv = view(bytes);
  const out: Mp4Sample[] = [];
  // trex defaults (moov/mvex/trex) apply when tfhd says nothing.
  const moov = top.find((b) => b.type === 'moov')!;
  const trex = find(bytes, moov, ['mvex', 'trex']);
  const trexDuration = trex ? dv.getUint32(trex.body + 12) : 0;
  const trexSize = trex ? dv.getUint32(trex.body + 16) : 0;
  for (const moof of top.filter((b) => b.type === 'moof')) {
    for (const traf of children(bytes, moof.body, moof.end).filter((b) => b.type === 'traf')) {
      const kids = children(bytes, traf.body, traf.end);
      const tfhd = kids.find((b) => b.type === 'tfhd');
      if (!tfhd) continue;
      const tfFlags = dv.getUint32(tfhd.body) & 0xffffff;
      let at = tfhd.body + 8; // version/flags + track_ID
      let base = moof.start; // default-base-is-moof, and the common MediaRecorder layout
      if (tfFlags & 0x000001) {
        base = Number(dv.getBigUint64(at));
        at += 8;
      }
      if (tfFlags & 0x000002) at += 4;
      let defaultDuration = trexDuration;
      let defaultSize = trexSize;
      if (tfFlags & 0x000008) {
        defaultDuration = dv.getUint32(at);
        at += 4;
      }
      if (tfFlags & 0x000010) {
        defaultSize = dv.getUint32(at);
        at += 4;
      }
      let cursor = -1;
      for (const trun of kids.filter((b) => b.type === 'trun')) {
        const flags = dv.getUint32(trun.body) & 0xffffff;
        const count = dv.getUint32(trun.body + 4);
        let p = trun.body + 8;
        if (flags & 0x000001) {
          cursor = base + dv.getInt32(p);
          p += 4;
        } else if (cursor < 0) {
          cursor = base;
        }
        if (flags & 0x000004) p += 4;
        for (let i = 0; i < count; i++) {
          let duration = defaultDuration;
          let size = defaultSize;
          if (flags & 0x000100) {
            duration = dv.getUint32(p);
            p += 4;
          }
          if (flags & 0x000200) {
            size = dv.getUint32(p);
            p += 4;
          }
          if (flags & 0x000400) p += 4;
          if (flags & 0x000800) p += 4;
          if (cursor + size > bytes.length) return out;
          out.push({ data: bytes.subarray(cursor, cursor + size), duration });
          cursor += size;
        }
      }
    }
  }
  return out;
}

/**
 * A fragmented MP4 (MediaRecorder's output) rewritten as a progressive one, keeping the codec's
 * own sample entry byte for byte. Null when the input cannot be read — the caller then sends the
 * original rather than nothing.
 */
export function remuxFragmented(bytes: Uint8Array): Uint8Array | null {
  const top = children(bytes, 0, bytes.length);
  const moov = top.find((b) => b.type === 'moov');
  if (!moov || !top.some((b) => b.type === 'moof')) return null;
  const trak = find(bytes, moov, ['trak']);
  const mdhd = trak && find(bytes, trak, ['mdia', 'mdhd']);
  const stsd = trak && find(bytes, trak, ['mdia', 'minf', 'stbl', 'stsd']);
  if (!mdhd || !stsd) return null;
  const dv = view(bytes);
  const timescale = bytes[mdhd.body] === 1 ? dv.getUint32(mdhd.body + 20) : dv.getUint32(mdhd.body + 12);
  const entry = children(bytes, stsd.body + 8, stsd.end)[0];
  if (!entry || !timescale) return null;
  const samples = readFragments(bytes, top, timescale);
  if (!samples.length) return null;
  return writeAudioMp4({ timescale, sampleEntry: bytes.slice(entry.start, entry.end), samples, majorBrand: 'M4A ' });
}
